import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaCheck,
  FaCheckDouble,
  FaChevronDown,
  FaChevronUp,
  FaPowerOff,
  FaTimes,
  FaUndo,
} from "react-icons/fa";
import {
  Badge,
  Box,
  Button,
  CardRoot,
  HStack,
  IconButton,
  Text,
  VStack,
} from "../../../../components/ui";
import {
  Suggestion,
  SuggestionEvidence,
  SuggestionKind,
  frictionPerSession,
} from "../../../../services/suggestionService";
import { readableAssistantText } from "../../../../utils/text";
import SkillDiff from "./SkillDiff";

export interface SuggestionGroupProps {
  /** Every kind proposed for one need (same clusterHash). */
  rows: Suggestion[];
  onAccept: (s: Suggestion) => void;
  onAcceptBoth: (shortcut: Suggestion, skill: Suggestion) => void;
  onDismiss: (s: Suggestion) => void;
  onRestore: (s: Suggestion) => void;
  onDisableTool: (s: Suggestion) => void;
  /** Accepts every pending row of the block as drafted — skills included, no dialog. */
  onAcceptAll: (rows: Suggestion[]) => void;
  onDismissAll: (rows: Suggestion[]) => void;
}

/**
 * Outline versions of the design system's primary / danger buttons: the same palette, just no fill.
 * The wrapper gives every outline button a neutral border, so the palette border is set explicitly.
 */
const OUTLINE_PRIMARY = {
  variant: "outline",
  colorPalette: "blue",
  color: "colorPalette.fg",
  borderColor: "colorPalette.solid",
  _hover: { bg: "colorPalette.subtle", borderColor: "colorPalette.solid" },
} as const;

const OUTLINE_DANGER = {
  ...OUTLINE_PRIMARY,
  colorPalette: "red",
} as const;

/** Scope / target badges truncate rather than push the title off the line. */
const SCOPE_BADGE_MAX_W = { base: "110px", xl: "160px" };

const KIND_COLOR: Record<SuggestionKind, string> = {
  Shortcut: "blue",
  Skill: "green",
  SkillAmendment: "teal",
  Toolkit: "orange",
  Kb: "purple",
  Schedule: "gray",
  Defect: "red",
};

const DAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const capitalise = (s: string): string =>
  s.length > 0 ? s[0].toUpperCase() + s.slice(1) : s;

/**
 * One need as a block: a header naming the need with the evidence and Accept all / Dismiss all, then
 * one line per proposed fix. A line expands into the full preview of what accepting does — the skill
 * instructions that will be created or appended, the tool that will be switched on, the schedule —
 * because the analytics can be wrong and nobody should accept what they cannot read.
 */
const SuggestionGroup: React.FC<SuggestionGroupProps> = ({
  rows,
  onAccept,
  onAcceptBoth,
  onDismiss,
  onRestore,
  onDisableTool,
  onAcceptAll,
  onDismissAll,
}) => {
  const { t } = useTranslation();
  const [openIds, setOpenIds] = useState<Record<string, boolean>>({});
  const lead = rows[0];
  const need = lead.draft?.intent
    ? capitalise(lead.draft.intent)
    : (lead.representativeQuestions[0] ?? lead.title);
  const groupEvidence = rows.map((r) => r.evidence).find((e) => e) ?? null;
  const pending = rows.filter((r) => r.status === "Pending");

  const toggle = (id: string): void =>
    setOpenIds((prev) => ({ ...prev, [id]: !prev[id] }));

  const evidenceLine = (e: SuggestionEvidence): string =>
    t("app.suggestions.group.evidenceLine", {
      sessions: e.sessions,
      users: e.distinctUsers,
      refusals: e.refusals,
      retries: e.retries,
      corrections: e.corrections,
    });

  // Rows copied from the old shortcut table carry counts but no evidence payload.
  const countsLine = (s: Suggestion): string =>
    t("app.suggestions.group.counts", {
      sessions: s.evidence?.sessions ?? s.occurrenceCount,
      users: s.evidence?.distinctUsers ?? s.distinctCustomerCount,
    });

  const outcomeLine = (s: Suggestion): string | null => {
    if (!s.outcome || !s.evidence) return null;
    const before = frictionPerSession(s.evidence);
    const after = frictionPerSession(s.outcome);
    const delta =
      before === 0 ? 0 : Math.round(((after - before) / before) * 100);
    return t("app.suggestions.outcome.line", {
      sessions: s.outcome.sessions,
      delta: `${delta > 0 ? "+" : ""}${delta}%`,
    });
  };

  const toolkitLine = (s: Suggestion): string | null =>
    s.kind === "Toolkit" && s.draft?.change
      ? t(`app.suggestions.toolkit.confirm.${s.draft.change}`, {
          tool: s.targetToolName ?? s.draft.toolDisplayName ?? "",
        })
      : null;

  /** What accepting this line does, in full. */
  const preview = (s: Suggestion): React.ReactNode => {
    const d = s.draft;
    const label = (key: string): React.ReactNode => (
      <Text fontSize="xs" fontWeight={600} color="var(--text-primary)">
        {t(`app.suggestions.preview.${key}`)}
      </Text>
    );
    const block = (
      text: string | null | undefined,
      mono = false,
    ): React.ReactNode =>
      text ? (
        <Box
          w="100%"
          px={3}
          py={2}
          borderWidth="1px"
          borderColor="var(--code-block-border)"
          borderRadius="6px"
          bg="var(--code-block-bg)"
        >
          <Text
            fontSize="xs"
            fontFamily={mono ? "mono" : undefined}
            whiteSpace="pre-wrap"
            color="var(--text-primary)"
          >
            {text}
          </Text>
        </Box>
      ) : null;

    switch (s.kind) {
      case "Shortcut":
        return (
          <>
            {label("shortcutText")}
            {block(s.promptBody, true)}
            {s.description && (
              <Text fontSize="xs" color="var(--text-secondary-color)">
                {s.description}
              </Text>
            )}
          </>
        );
      case "Skill":
        return (
          <>
            <Text fontSize="xs" color="var(--text-secondary-color)">
              {t("app.suggestions.preview.skillName", {
                name: d?.name ?? s.title,
              })}
              {d?.category ? ` · ${d.category}` : ""}
            </Text>
            {d?.humanReadableDescription && (
              <Text fontSize="xs" color="var(--text-secondary-color)">
                {d.humanReadableDescription}
              </Text>
            )}
            {label("trigger")}
            {block(d?.triggerContext)}
            {label("instructions")}
            {block(d?.skillPrompt, true)}
          </>
        );
      case "SkillAmendment": {
        const current = s.targetSkillPrompt ?? "";
        const addition = d?.skillPrompt ?? "";
        const proposed =
          current.length > 0
            ? `${current.replace(/\s+$/, "")}\n\n${addition}`
            : addition;
        return (
          <>
            <Text fontSize="xs" color="var(--text-secondary-color)">
              {t("app.suggestions.preview.appendTo", {
                skill: s.targetSkillName ?? d?.targetSkillName ?? "",
              })}
            </Text>
            <SkillDiff current={current} proposed={proposed} />
          </>
        );
      }
      case "Toolkit":
        return (
          <Text fontSize="xs" color="var(--text-primary)">
            {toolkitLine(s)}
          </Text>
        );
      case "Defect":
        return (
          <Text fontSize="xs" color="var(--text-primary)">
            {t("app.suggestions.preview.defect", {
              tool: s.targetToolName ?? d?.toolDisplayName ?? "",
              empty: d?.emptyLoops ?? 0,
              failures: d?.toolFailures ?? 0,
            })}
          </Text>
        );
      case "Schedule":
        return (
          <>
            <Text fontSize="xs" color="var(--text-primary)">
              {t("app.suggestions.preview.schedule", {
                day: t(
                  `app.suggestions.preview.days.${DAY_KEYS[d?.dayOfWeek ?? 0]}`,
                ),
                time: d?.timeOfDay ?? "",
              })}
            </Text>
            {d?.tools && d.tools.length > 0 && (
              <Text fontSize="xs" color="var(--text-secondary-color)">
                {t("app.suggestions.preview.tools", {
                  tools: d.tools.join(", "),
                })}
              </Text>
            )}
          </>
        );
      case "Kb":
        return (
          <>
            {label("questions")}
            {(d?.questions ?? s.representativeQuestions).map((q, i) => (
              <Text key={i} fontSize="xs" color="var(--text-secondary-color)">
                “{q}”
              </Text>
            ))}
          </>
        );
      default:
        return null;
    }
  };

  return (
    // Same surface as the dashboard's KPI tiles: CardRoot's flat tone — panel background, card
    // border, card radius. The header and rows are transparent so the block reads as one card.
    <CardRoot overflow="hidden">
      <HStack
        px={3}
        py={1.5}
        gap={3}
        align="center"
        borderBottomWidth="1px"
        borderColor="var(--card-border)"
      >
        <Box flex="1" minW={0}>
          <Text
            fontSize="sm"
            fontWeight={600}
            color="var(--text-primary)"
            truncate
          >
            {need}
          </Text>
          {groupEvidence && (
            <Text fontSize="xs" color="var(--text-secondary-color)" truncate>
              {evidenceLine(groupEvidence)}
            </Text>
          )}
        </Box>
        {pending.length > 1 && (
          <HStack gap={1} flexShrink={0}>
            <Button
              size="xs"
              {...OUTLINE_PRIMARY}
              onClick={() => onAcceptAll(pending)}
            >
              <FaCheckDouble /> {t("app.suggestions.group.acceptAll")}
            </Button>
            <Button
              size="xs"
              intent="secondary"
              onClick={() => onDismissAll(pending)}
            >
              <FaTimes /> {t("app.suggestions.group.dismissAll")}
            </Button>
          </HStack>
        )}
      </HStack>

      {rows.map((s, i) => {
        const dependsOn = s.dependsOnSuggestionId
          ? rows.find((r) => r.id === s.dependsOnSuggestionId)
          : undefined;
        const isOpen = !!openIds[s.id];
        const outcome = outcomeLine(s);
        // What accepting does is the deciding fact for a toolkit change; for the rest it is why.
        const summary = toolkitLine(s) ?? s.rationale;
        return (
          <Box
            key={s.id}
            borderTopWidth={i > 0 ? "1px" : 0}
            borderColor="var(--card-border)"
          >
            <HStack gap={3} px={3} py={1.5} align="center">
              <HStack gap={1} flexShrink={0}>
                <Badge colorPalette={KIND_COLOR[s.kind]} size="sm">
                  {t(`app.suggestions.kinds.${s.kind}`)}
                </Badge>
                {s.targetSkillName && (
                  <Badge
                    colorPalette="gray"
                    size="sm"
                    maxW={SCOPE_BADGE_MAX_W}
                    truncate
                    display="block"
                  >
                    {s.targetSkillName}
                  </Badge>
                )}
                {s.targetToolName && (
                  <Badge
                    colorPalette="gray"
                    size="sm"
                    maxW={SCOPE_BADGE_MAX_W}
                    truncate
                    display="block"
                  >
                    {s.targetToolName}
                  </Badge>
                )}
              </HStack>

              <HStack
                gap={2}
                flex="1"
                minW={0}
                align="baseline"
                cursor="pointer"
                onClick={() => toggle(s.id)}
              >
                {/* The title keeps its natural width; the summary only gets what is left over, so
                    it is the first thing to give way when the window narrows. */}
                <Text
                  fontSize="sm"
                  fontWeight={500}
                  color="var(--text-primary)"
                  flex="0 1 auto"
                  minW="6em"
                  truncate
                >
                  {s.title}
                </Text>
                <Text
                  fontSize="xs"
                  color="var(--text-secondary-color)"
                  flex="1 1 0"
                  minW={0}
                  truncate
                  display={{ base: "none", md: "block" }}
                >
                  {summary}
                </Text>
              </HStack>

              <Text
                fontSize="xs"
                color="var(--text-secondary-color)"
                whiteSpace="nowrap"
                flexShrink={0}
                display={{ base: "none", xl: "block" }}
              >
                {countsLine(s)}
              </Text>

              <HStack gap={1} flexShrink={0}>
                {s.status === "Pending" ? (
                  <>
                    {s.kind === "Defect" ? (
                      <Button
                        size="xs"
                        {...OUTLINE_DANGER}
                        onClick={() => onDisableTool(s)}
                      >
                        <FaPowerOff /> {t("app.suggestions.defect.disableTool")}
                      </Button>
                    ) : (
                      <Button
                        size="xs"
                        {...OUTLINE_PRIMARY}
                        onClick={() => onAccept(s)}
                      >
                        <FaCheck /> {t("app.suggestions.accept")}
                      </Button>
                    )}
                    {dependsOn && dependsOn.status === "Pending" && (
                      <Button
                        size="xs"
                        {...OUTLINE_PRIMARY}
                        onClick={() => onAcceptBoth(s, dependsOn)}
                      >
                        <FaCheck /> {t("app.suggestions.acceptBoth")}
                      </Button>
                    )}
                    <Button
                      size="xs"
                      intent="secondary"
                      onClick={() => onDismiss(s)}
                    >
                      <FaTimes /> {t("app.suggestions.dismiss")}
                    </Button>
                  </>
                ) : (
                  <Button
                    size="xs"
                    intent="secondary"
                    onClick={() => onRestore(s)}
                  >
                    <FaUndo /> {t("app.suggestions.restore")}
                  </Button>
                )}
                <IconButton
                  aria-label={t(
                    isOpen
                      ? "app.suggestions.row.hideDetails"
                      : "app.suggestions.row.showDetails",
                  )}
                  size="xs"
                  variant="ghost"
                  onClick={() => toggle(s.id)}
                >
                  {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                </IconButton>
              </HStack>
            </HStack>

            {isOpen && (
              <VStack align="start" gap={1.5} px={3} pb={3} pt={0.5}>
                <Text
                  fontSize="xs"
                  fontWeight={600}
                  color="var(--text-primary)"
                >
                  {t("app.suggestions.preview.heading")}
                </Text>
                {preview(s)}
                {dependsOn && (
                  <Text fontSize="xs" color="var(--text-secondary-color)">
                    {t("app.suggestions.dependsOn", { skill: dependsOn.title })}
                  </Text>
                )}

                <Text
                  fontSize="xs"
                  fontWeight={600}
                  color="var(--text-primary)"
                  pt={1}
                >
                  {t("app.suggestions.preview.why")}
                </Text>
                <Text fontSize="xs" color="var(--text-secondary-color)">
                  {s.rationale}
                </Text>
                <Text fontSize="xs" color="var(--text-secondary-color)">
                  {s.evidence ? evidenceLine(s.evidence) : countsLine(s)}
                </Text>
                {s.representativeQuestions.slice(0, 3).map((q, qi) => (
                  <Text
                    key={`q${qi}`}
                    fontSize="xs"
                    color="var(--text-secondary-color)"
                    fontStyle="italic"
                  >
                    “{q}”
                  </Text>
                ))}
                {s.evidence?.quotes.slice(0, 2).map((q, qi) => (
                  <Text
                    key={`e${qi}`}
                    fontSize="xs"
                    color="var(--text-secondary-color)"
                  >
                    {t("app.suggestions.preview.assistantSaid")}: “
                    {readableAssistantText(q)}”
                  </Text>
                ))}
                {outcome && (
                  <Text fontSize="xs" color="var(--text-primary)">
                    {outcome}
                  </Text>
                )}
              </VStack>
            )}
          </Box>
        );
      })}
    </CardRoot>
  );
};

export default SuggestionGroup;
